/**
 * Vorlaufstaffel (Board „Preisautomatik · Einstellungen“): Schalter, bis zu fünf Stufen als Eingaben,
 * beim Verlassen nach Vorlauf sortiert, und ein Beispiel. Im Modus „Aus“ bleibt sie gespeichert,
 * wirkt aber nicht und ist gesperrt.
 */
import type { UseFieldArrayReturn, UseFormReturn } from 'react-hook-form';
import { ConfigurationField, ConfigurationSwitch } from './ConfigurationField';
import type { PricingConfigurationFormValues } from './pricingConfigurationSchema';
import { tierExample } from './tierExample';

interface Props {
  form: UseFormReturn<PricingConfigurationFormValues>;
  tiers: UseFieldArrayReturn<PricingConfigurationFormValues, 'minimumStayLeadTimeTiers'>;
  errorFor: (path: string) => string | undefined;
}

export function MinimumStayTierControls({ form, tiers, errorFor }: Props) {
  const off = form.watch('mode') === 'Off';
  const enabled = form.watch('minimumStayTiersEnabled') && !off;
  // Gespeicherte Stufen bleiben sichtbar, auch ausgeschaltet – sie gehen mit und werden geprüft.
  const visible = enabled || tiers.fields.length > 0;
  const values = form.watch('minimumStayLeadTimeTiers');
  const sortOnLeave = () => {
    const current = form.getValues('minimumStayLeadTimeTiers');
    const sorted = [...current].sort(
      (left, right) => Number(left.daysBeforeArrival) - Number(right.daysBeforeArrival),
    );
    if (current.some((tier, index) => tier !== sorted[index])) tiers.replace(sorted);
  };
  const append = () => {
    const thresholds = values.map((tier) => Number(tier.daysBeforeArrival)).filter(Number.isFinite);
    tiers.append({
      daysBeforeArrival: String(Math.max(-1, ...thresholds) + 1),
      minimumStayNights: '1',
    });
  };
  const listError = errorFor('minimumStayLeadTimeTiers');
  return (
    <div className="flex flex-col gap-2.5">
      <ConfigurationSwitch
        label="Vorlaufstaffel"
        hint={
          off
            ? 'Im Modus „Aus“ bleibt die Staffel gespeichert, wirkt aber nicht.'
            : 'Je kurzfristiger die Anreise, desto weniger Mindestnächte.'
        }
        disabled={off}
        {...form.register('minimumStayTiersEnabled')}
      />
      {errorFor('minimumStayTiersEnabled') && (
        <p className="text-sealwax text-xs">{errorFor('minimumStayTiersEnabled')}</p>
      )}
      {visible && (
        <div className="border-hairline overflow-hidden rounded-lg border">
          {tiers.fields.map((tier, index) => {
            const path = `minimumStayLeadTimeTiers.${index}` as const;
            const days = form.register(`${path}.daysBeforeArrival`);
            const nights = form.register(`${path}.minimumStayNights`);
            return (
              <div
                key={tier.id}
                className="border-card-alt grid items-end gap-2.5 border-b p-3 sm:grid-cols-[4rem_1fr_1fr_auto]"
              >
                <b className="self-center text-sm">Stufe {index + 1}</b>
                <ConfigurationField
                  label={`Vorlauf bis (Stufe ${index + 1})`}
                  type="number"
                  inputMode="numeric"
                  min="0"
                  suffix="Tage"
                  disabled={off}
                  error={errorFor(`${path}.daysBeforeArrival`)}
                  {...days}
                  onBlur={(event) => void days.onBlur(event).then(sortOnLeave)}
                />
                <ConfigurationField
                  label={`Höchstens (Stufe ${index + 1})`}
                  type="number"
                  inputMode="numeric"
                  min="1"
                  max="30"
                  suffix="Nächte"
                  disabled={off}
                  error={errorFor(`${path}.minimumStayNights`)}
                  {...nights}
                  onBlur={(event) => void nights.onBlur(event).then(sortOnLeave)}
                />
                <button
                  type="button"
                  disabled={off}
                  onClick={() => tiers.remove(index)}
                  className="text-sealwax rounded-pill min-h-11 px-3 text-xs font-bold disabled:opacity-40"
                >
                  Stufe {index + 1} entfernen
                </button>
              </div>
            );
          })}
          <div className="bg-paper flex flex-wrap items-center gap-2.5 px-3.5 py-2.5">
            <span className="text-ink-soft grow text-xs" aria-live="polite">
              {tierExample(values)}
            </span>
            {!off && tiers.fields.length < 5 && (
              <button
                type="button"
                onClick={append}
                className="border-forest text-forest rounded-pill min-h-11 border-[1.5px] border-dashed px-3 text-xs font-bold"
              >
                + Stufe hinzufügen
              </button>
            )}
          </div>
          {listError && <p className="text-sealwax px-3.5 pb-2.5 text-xs">{listError}</p>}
        </div>
      )}
    </div>
  );
}
